import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Text } from '@reservamos/elements';
import './ExchangeLineSelector.scss';

// Fallback options per row, used until the options can be measured (and in tests, where nothing
// has a size).
const OPTIONS_PER_ROW = 4;
// Rows shown before "see more", whose toggle takes the last slot of the last row.
const COLLAPSED_ROWS = 2;
// Packing needs to watch sizes; without ResizeObserver the fixed fallback is used instead.
const CAN_MEASURE = typeof window !== 'undefined' && 'ResizeObserver' in window;
// Must match `.exchange-line-options-row`: its inner padding and the gap between options.
const ROW_PADDING = 4;
const OPTION_GAP = 4;

/**
 * Identifies a line within `exchanges/lines`. The endpoint answers one entry per line, not per
 * transporter, so several lines can share a `transporterAbbr` — the abbr alone would select (and
 * key) all of them at once. Name plus abbr is unique per entry.
 * @param {Object} line - Exchangeable line from `exchanges/lines`
 * @returns {String} - Key unique to that line
 */
export const getLineKey = (line) => `${line.transporterAbbr}:${line.name}`;

/**
 * Width a row of options takes, track padding included.
 * @param {Number[]} widths - Width of each option in the row
 * @returns {Number} - The row's width
 */
const rowWidth = (widths) =>
  widths.reduce((total, width) => total + width, 0) +
  OPTION_GAP * Math.max(widths.length - 1, 0) +
  ROW_PADDING * 2;

/**
 * Splits the options into rows: as many per row as fit the available width when they have been
 * measured, otherwise a fixed number per row.
 * @param {Number} count - Number of options
 * @param {Object|null} metrics - `{ available, widths }` once measured, `null` before
 * @returns {Number[][]} - Option indexes per row
 */
const packRows = (count, metrics) => {
  const rows = [];
  for (let index = 0; index < count; index += 1) {
    const current = rows[rows.length - 1];
    const fits = metrics
      ? current &&
        rowWidth([...current.map((i) => metrics.widths[i]), metrics.widths[index]]) <=
          metrics.available
      : current && current.length < OPTIONS_PER_ROW;
    if (fits) current.push(index);
    else rows.push([index]);
  }
  return rows;
};

/**
 * Lays the options out in rows and decides whether the list is collapsed behind "see more".
 * Collapsed, it keeps the first rows and frees room in the last one for the toggle. It never
 * collapses over the chosen line.
 * @param {Object} params - Layout inputs
 * @param {Number} params.count - Number of options
 * @param {Object|null} params.metrics - `{ available, widths }` once measured; the last width is
 *   the toggle's
 * @param {Boolean} params.expanded - Whether "see more" was pressed
 * @param {Number} params.selectedIndex - Index of the chosen line, -1 when none
 * @returns {{ rows: Number[][], isCollapsed: Boolean }} - Rows to render and whether to add the
 *   toggle
 */
const layoutRows = ({ count, metrics, expanded, selectedIndex }) => {
  const rows = packRows(count, metrics);
  if (expanded || rows.length <= COLLAPSED_ROWS) return { rows, isCollapsed: false };

  const kept = rows.slice(0, COLLAPSED_ROWS).map((row) => [...row]);
  const last = kept[kept.length - 1];
  if (metrics) {
    const toggleWidth = metrics.widths[count];
    const lastWidths = () => last.map((i) => metrics.widths[i]);
    while (last.length > 1 && rowWidth([...lastWidths(), toggleWidth]) > metrics.available) {
      last.pop();
    }
  } else {
    last.pop();
  }

  const visibleCount = kept.reduce((total, row) => total + row.length, 0);
  if (selectedIndex >= visibleCount) return { rows, isCollapsed: false };
  return { rows: kept, isCollapsed: true };
};

/**
 * One selectable line. The logo is optional (`logo_url` can be null) and can also fail to load,
 * so the name is always rendered and the image is treated as decoration on top of it.
 * @param {Object} props - Component props
 * @param {Object} props.line - Exchangeable line from `exchanges/lines`
 * @param {Boolean} props.isSelected - Whether this line is the chosen one
 * @param {Function} props.onSelect - Called with the line's key (see `getLineKey`)
 * @returns Component
 */
const ExchangeLineOption = ({ line, isSelected, onSelect }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = Boolean(line.logoUrl) && !logoFailed;

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      className={`exchange-line-option ${isSelected ? 'is-selected' : ''}`}
      onClick={() => onSelect(getLineKey(line))}
    >
      {showLogo && (
        <img
          className="exchange-line-option-logo"
          src={line.logoUrl}
          alt=""
          onError={() => setLogoFailed(true)}
        />
      )}
      <Text size="S" weight={isSelected ? 'semibold' : 'regular'}>
        {line.name}
      </Text>
    </button>
  );
};

ExchangeLineOption.propTypes = {
  line: PropTypes.shape({
    name: PropTypes.string,
    transporterAbbr: PropTypes.string,
    logoUrl: PropTypes.string,
  }).isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

/**
 * ExchangeLineSelector component.
 * Line picker for the exchange form: rows of rounded options, each showing a line's logo and
 * commercial name (task 8.2/8.3 of openspec/changes/iamsa-b2c-ticket-exchange, platform-sre).
 * Rendered above the form because the chosen line decides which form is shown.
 *
 * Options are packed into as many per row as fit, each row on its own track. Past two rows the
 * list collapses behind "see more"; a chosen line is never hidden, so when it sits past the cut
 * the list stays open.
 * @param {Object} props - Component props
 * @param {Object[]} props.lines - Exchangeable lines for the current tenant
 * @param {String} props.value - Key of the line currently selected (see `getLineKey`)
 * @param {Function} props.onChange - Called with the newly selected line's key
 * @param {String} props.label - Section label rendered above the options
 * @returns Component
 */
const ExchangeLineSelector = ({ lines, value, onChange, label }) => {
  const { t } = useTranslation('exchange');
  const [expanded, setExpanded] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const optionsRef = useRef(null);
  const measureRef = useRef(null);

  // Rows are packed from what each option really measures, so a row never wraps inside its
  // track. The hidden copy below is measured again whenever it resizes, which also covers logos
  // that change an option's width once they load.
  useLayoutEffect(() => {
    if (!CAN_MEASURE) return undefined;
    const measure = () => {
      const available = optionsRef.current?.clientWidth ?? 0;
      const widths = Array.from(measureRef.current?.children ?? [], (el) => el.offsetWidth);
      setMetrics(available && widths.every(Boolean) ? { available, widths } : null);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(optionsRef.current);
    observer.observe(measureRef.current);
    return () => observer.disconnect();
  }, [lines]);

  const selectedIndex = lines.findIndex((line) => getLineKey(line) === value);
  const { rows, isCollapsed } = layoutRows({
    count: lines.length,
    metrics,
    expanded,
    selectedIndex,
  });

  return (
    <div className="exchange-line-selector">
      <Text size="S" mobileSize="S">
        {label}
      </Text>
      <div ref={optionsRef} className="exchange-line-options" role="radiogroup" aria-label={label}>
        {rows.map((row, rowIndex) => (
          // Rows are rebuilt from the same list on every render; their position is their identity.
          // eslint-disable-next-line react/no-array-index-key
          <div key={rowIndex} className="exchange-line-options-row">
            {row.map((index) => (
              <ExchangeLineOption
                key={getLineKey(lines[index])}
                line={lines[index]}
                isSelected={index === selectedIndex}
                onSelect={onChange}
              />
            ))}
            {isCollapsed && rowIndex === rows.length - 1 && (
              <button
                type="button"
                className="exchange-line-see-more"
                onClick={() => setExpanded(true)}
              >
                {t('button.see_more_lines')}
              </button>
            )}
          </div>
        ))}
      </div>
      {/* Same options, laid out in one line off screen, only to read how wide each one is. The
          last child is the "see more" toggle. */}
      {CAN_MEASURE && (
        <div ref={measureRef} className="exchange-line-measure" aria-hidden="true">
          {lines.map((line) => (
            <span key={getLineKey(line)} className="exchange-line-option is-selected">
              {Boolean(line.logoUrl) && (
                <img className="exchange-line-option-logo" src={line.logoUrl} alt="" />
              )}
              <Text size="S" weight="semibold">
                {line.name}
              </Text>
            </span>
          ))}
          <span className="exchange-line-see-more">{t('button.see_more_lines')}</span>
        </div>
      )}
    </div>
  );
};

ExchangeLineSelector.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  lines: PropTypes.arrayOf(PropTypes.object).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
};

export default ExchangeLineSelector;
